import { authClient, getSlackEmbedKeycloakInitOptions } from './auth-client';
import { parseSlackEmbedTarget, type SlackEmbedTarget } from './slack-embed-location';
import type { SlackEmbedErrorCode } from './slack-embed-render-api';

export const SLACK_EMBED_SESSION_PATH = '/api/slack/embed/session';

export class SlackEmbedSessionError extends Error {
  readonly code: SlackEmbedErrorCode;

  constructor(code: SlackEmbedErrorCode) {
    super(code);
    this.name = 'SlackEmbedSessionError';
    this.code = code;
  }
}

export type SlackEmbedTokens = {
  accessToken: string;
  refreshToken: string;
  idToken?: string;
};

type SlackEmbedSessionResponse = Pick<Response, 'ok' | 'status' | 'json'>;

type FetchFn = (input: string, init?: RequestInit) => Promise<SlackEmbedSessionResponse>;

const defaultFetch: FetchFn = (input, init) => fetch(input, init);

export async function redeemSlackEmbedCode(
  code: string,
  target: SlackEmbedTarget,
  fetchFn: FetchFn = defaultFetch,
): Promise<SlackEmbedTokens> {
  let response: SlackEmbedSessionResponse;

  try {
    response = await fetchFn(new URL(SLACK_EMBED_SESSION_PATH, window.location.origin).toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, target }),
      credentials: 'omit',
      cache: 'no-store',
    });
  } catch {
    throw new SlackEmbedSessionError('error');
  }

  if (!response.ok) {
    throw new SlackEmbedSessionError(
      mapSlackEmbedRedeemFailure(response.status, await readErrorCode(response)),
    );
  }

  const body: unknown = await response.json().catch(() => undefined);
  const tokens = parseSlackEmbedTokens(body);

  if (!tokens) {
    throw new SlackEmbedSessionError('error');
  }

  return tokens;
}

export function mapSlackEmbedRedeemFailure(
  status: number,
  errorCode: string | undefined,
): SlackEmbedErrorCode {
  if (status === 410) {
    return 'session_expired';
  }

  if (status === 403 && (errorCode === 'no_access' || errorCode === 'user_disabled')) {
    return 'no_access';
  }

  if (status === 404) {
    return 'not_found';
  }

  return 'error';
}

export function parseSlackEmbedTokens(body: unknown): SlackEmbedTokens | undefined {
  if (!isRecord(body)) {
    return undefined;
  }

  const { accessToken, refreshToken, idToken } = body;

  if (!isNonEmptyString(accessToken) || !isNonEmptyString(refreshToken)) {
    return undefined;
  }

  return {
    accessToken,
    refreshToken,
    idToken: isNonEmptyString(idToken) ? idToken : undefined,
  };
}

/**
 * Redeems the single-use code (taken from the URL fragment by `consumeSlackEmbedCode` at startup)
 * and initializes the Keycloak client with the returned tokens. Never redirects to the Keycloak
 * login page.
 */
export async function initSlackEmbedAuth(
  code: string | undefined,
  fetchFn: FetchFn = defaultFetch,
): Promise<boolean> {
  const target = parseSlackEmbedTarget(window.location.pathname);

  if (!target) {
    throw new SlackEmbedSessionError('not_found');
  }

  if (!code) {
    throw new SlackEmbedSessionError('session_expired');
  }

  const tokens = await redeemSlackEmbedCode(code, target, fetchFn);

  return authClient.init(getSlackEmbedKeycloakInitOptions(tokens));
}

/**
 * Replaces the interactive login flows of the Keycloak client: in the Slack embed the iframe must
 * never navigate to the login page, so any session end is reported to `onSessionEnded` instead.
 */
export function installSlackEmbedAuthGuards(
  onSessionEnded: (code: SlackEmbedErrorCode) => void,
): void {
  authClient.onAuthRefreshError = () => {
    // A transient refresh failure keeps the tokens; only a lost or expired session ends the embed.
    if (!authClient.authenticated || isAccessTokenExpired()) {
      onSessionEnded('session_expired');
    }
  };

  authClient.onAuthLogout = () => onSessionEnded('session_expired');

  authClient.login = () => {
    onSessionEnded('session_expired');
    return createResolvedLoginResult();
  };
}

type LoginResult = ReturnType<typeof authClient.login>;

function createResolvedLoginResult(): LoginResult {
  const result: LoginResult = Object.assign(Promise.resolve(), {
    success(callback: () => void): LoginResult {
      void result.then(callback);
      return result;
    },
    error(): LoginResult {
      return result;
    },
  });

  return result;
}

function isAccessTokenExpired(): boolean {
  try {
    return authClient.isTokenExpired();
  } catch {
    return true;
  }
}

async function readErrorCode(response: SlackEmbedSessionResponse): Promise<string | undefined> {
  const body: unknown = await response.json().catch(() => undefined);

  if (isRecord(body) && typeof body.error === 'string') {
    return body.error;
  }

  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}
